import React from 'react';
import { TextField } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { bool, string } from 'prop-types';
import THEME from '../../../constants/theme';

const useStyles = makeStyles(() => ({
  root: {
    borderRadius: '6px',
    width: '100%',
    margin: ({ margin }) => margin || '0',
    '& input:-internal-autofill-selected': {
      boxShadow: '0 0 0px 1000px white inset',
    },
    '& label': {
      color: THEME.greyColors.grey1,
      fontWeight: 'normal',
      fontSize: ({ size }) => (size === 'small' ? '14' : '16'),
      zIndex: 2,
    },
    '& label.Mui-focused': {
      color: `${THEME.greyColors.grey600}!important`,
    },
    '&.has-error': {
      '& label': {
        color: `${THEME.secondaryColors.nodeError}!important`,
      },
      '& .MuiOutlinedInput-root fieldset': {
        borderColor: `${THEME.secondaryColors.nodeError}`,
        zIndex: 1,
      },
    },
    '& .MuiInput-underline:after': {
      borderBottomColor: THEME.primaryColors.black,
    },
    '& .MuiOutlinedInput-input': {
      '&.Mui-disabled': {
        color: `${THEME.greyColors.grey13}`,
      },
      '&:hover': {
        color: ({ hover }) => (hover ? THEME.primaryColors.blue : 'none'),
      },
      padding: ({ size, padding }) => {
        if (padding) {
          return padding;
        }
        if (size === 'small') {
          return '8px 16px 11px';
        }
        return '9px 16px 11px';
      },
      paddingLeft: ({ innerPaddingLeft }) => innerPaddingLeft || '16px',
      fontWeight: ({ fontWeight }) => fontWeight,
      fontSize: ({ size, fontSize }) => {
        if (fontSize) {
          return fontSize;
        }
        if (size === 'small') {
          return '14px';
        }
        return '18px';
      },
    },
    '& .MuiInputLabel-root': {
      fontSize: ({ size }) => {
        if (size === 'smaller') {
          return '14px';
        }
        return '18px';
      },
    },
    '& .MuiOutlinedInput-inputMultiline': {
      padding: '0',
      alignSelf: 'flex-start',
    },
    '& .MuiInputBase-input': {
      width: ({ width }) => width,
      height: ({ multiline }) => multiline ?? 28,
      color: ({ inputColor }) => inputColor || 'black',
      position: 'relative',
      transform: 'translateY(9px)',
      zIndex: 1,
      fontWeight: ({ fontWeight }) => fontWeight,
      lineHeight: ({ lineHeight }) => lineHeight,
      '&::placeholder': {
        color: THEME.greyColors.grey1,
        opacity: 1,
      },
    },
    '& .MuiInputLabel-outlined': {
      transform: ({ size }) => {
        if (size === 'smaller') {
          return 'translate(16px, 17px) scale(1)';
        }
        return 'translate(16px, 20px) scale(1)';
      },
      '&.MuiInputLabel-shrink.MuiFormLabel-filled': {
        transform: 'translate(14px, 10px) scale(0.8)',
        fontWeight: 400,
        padding: '0 4px!important',
        marginLeft: '-2px',
        color: '#6A6A6A',
      },
      '&.MuiInputLabel-shrink.Mui-focused': {
        transform: 'translate(14px, 10px) scale(0.8)',
        fontWeight: 400,
        background: 'white',
        padding: '0 4px!important',
        marginLeft: '-2px',
        borderColor: THEME.primaryColors.blue,
      },
    },
    '& .MuiInputAdornment-root': {
      zIndex: 999,
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: '6px',
      height: ({ height, multiline }) => (multiline ? undefined : height || '40px'),
      '& fieldset': {
        backgroundColor: 'transparent !important',
        borderColor: THEME.greyColors.grey16,
        borderWidth: ({ noBorder }) => (noBorder ? 0 : 1),
        borderRadius: ({ borderRadius }) => `${borderRadius || '6px'} !important`,
        top: 0,
        zIndex: 2,
        '& legend': {
          display: 'none',
        },
      },
      '&:hover fieldset': {
        borderColor: THEME.greyColors.grey300,
      },
      '&.Mui-disabled': {
        '& fieldset': {
          borderColor: '#F5F6F7',
        },
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: THEME.primaryColors.primary,
        borderWidth: ({ noBorder }) => (noBorder ? 0 : 1),
      },
      '&.Mui-focused fieldset': {
        borderWidth: ({ noBorder }) => (noBorder ? 0 : 1),
      },
    },
    '& .MuiOutlinedInput-root fieldset': {
      borderRadius: 8,
    },
    '& .MuiInputBase-root': {
      height: ({ height }) => height ?? '40px',
    },
    borderWidth: ({ noBorder }) => (noBorder ? 0 : 1),
  },
}));

const InputFieldOnboarding = ({
  noBorder,
  innerPaddingLeft,
  margin,
  height,
  width,
  lineHeight,
  fontSize,
  fontWeight,
  padding,
  hover,
  multiline,
  ...props
}) => {
  const classes = useStyles({
    noBorder,
    innerPaddingLeft,
    margin,
    height,
    width,
    lineHeight,
    fontSize,
    fontWeight,
    padding,
    hover,
    multiline,
  });

  return <TextField variant="standard" {...props} multiline={multiline} classes={classes} />;
};

InputFieldOnboarding.propTypes = {
  noBorder: bool,
  innerPaddingLeft: string,
  margin: string,
  height: string,
  width: string,
  lineHeight: string,
  fontSize: string,
  fontWeight: string,
  padding: string,
  hover: bool,
  multiline: bool,
};

InputFieldOnboarding.defaultProps = {
  noBorder: false,
  innerPaddingLeft: null,
  margin: null,
  height: undefined,
  width: undefined,
  lineHeight: undefined,
  fontSize: undefined,
  fontWeight: undefined,
  padding: undefined,
  hover: false,
  multiline: false,
};

export default InputFieldOnboarding;
